export type AuditLogObjectType = 'annotation'; // iteration 1: annotation only

export type AuditLogAction = string;

export type AuditLogSourceType =
  | 'user'
  | 'email'
  | 'hook'
  | 'system'
  | 'formula_fields'
  | 'rules';

// Why the source acted. Open-ended on the wire; known values listed for reference.
// Known: 'import' | 'reimport' | 'ai_extraction'.
export type AuditLogSourceAction = string;

export type AuditLogRuleRef = {
  ruleId?: number | null;
  ruleName?: string | null;
};

// Flat all-nullable bag (camelized 1:1 from the snake_case wire). FE narrows by `sourceType`.
export type AuditLogSourceDetails = {
  userId?: number | null;
  userEmail?: string | null;
  hookId?: number | null;
  hookName?: string | null;
  event?: string | null;
  systemAction?: string | null;
  rules?: AuditLogRuleRef[] | null;
  emailSender?: string | null;
  emailSubject?: string | null;
  action?: AuditLogSourceAction | null;
};

export type AuditLogDocumentRef = { id: number; filename: string };
export type AuditLogAutomationBlockerRef = {
  id: number;
  type: string;
  fields: string[];
};
export type AuditLogAssigneeRef = { id: number; email: string };

// Confirmed by BE (2026-06-23): status + the relation fields. The trailing catch-all keeps
// parsing lenient for fields not yet shaped (schema / metadata / messages / einvoice).
export type AnnotationFieldChange =
  | { field: 'status'; previousValue: string | null; value: string | null }
  // Bare ids on the wire; display names come from `ContentChanges.meta.queueNames`
  // (FE falls back to #<id> when the lookup misses).
  | {
      field: 'queue_id';
      previousValue: number | null;
      value: number | null;
    }
  | {
      field: 'document';
      previousValue: AuditLogDocumentRef | null;
      value: AuditLogDocumentRef | null;
    }
  | {
      field: 'automation_blocker';
      previousValue: AuditLogAutomationBlockerRef | null;
      value: AuditLogAutomationBlockerRef | null;
    }
  | {
      field: 'assignees';
      previousValue: AuditLogAssigneeRef[];
      value: AuditLogAssigneeRef[];
    }
  | { field: string; previousValue?: unknown; value?: unknown };

// Header-field datapoint value change (wire shape observed on develop 2026-08-03).
// The wire no longer carries datapoint ids or inline labels — labels arrive separately
// in `ContentChanges.meta.schemaIdLabels`.
export type ContentFieldChange = {
  schemaId: string;
  previousValue: string | null;
  value: string | null;
};

// A multivalue / line-item cell edit, addressed by table schema id and row id
// (wire shape observed on develop 2026-08-28). Multivalues and line-item tables
// share this shape; the wire carries no flag to tell them apart.
export type ContentTableRowChange = {
  rowId: number;
  fields: ContentFieldChange[];
};
export type ContentTableChange = {
  schemaId: string;
  rows: ContentTableRowChange[];
};

// Datapoint (`content`) changes container. `data.fields` are header-field edits,
// `data.tables` multivalue / line-item cell edits.
// `meta` carries display lookups for the whole entry: `schemaIdLabels` maps schema id ->
// label, `queueNames` maps queue id (stringified number) -> queue name (for the
// `queue_id` change in the sibling `annotation` array). Record keys are preserved as-is
// by the deserializer (never camelized).
export type ContentChanges = {
  data: {
    fields: ContentFieldChange[];
    tables?: ContentTableChange[];
  };
  meta?: {
    schemaIdLabels?: Record<string, string>;
    queueNames?: Record<string, string>;
  };
};

// Annotation-level changes container (wire shape observed on develop 2026-08-06):
// the same `{ data, meta }` envelope as `content`, replacing the earlier bare array.
// `meta` has only been observed empty so far; `queueNames` is anticipated to move here
// from `content.meta` (FE reads both) — kept lenient.
export type AnnotationChanges = {
  data: AnnotationFieldChange[];
  meta?: {
    queueNames?: Record<string, string>;
  };
};

export type AuditLogChanges = {
  annotation?: AnnotationChanges; // iteration 1: render `status`, parse the rest
  content?: ContentChanges; // iteration 1: header-field datapoint value changes
  document?: unknown[]; // 🔸 later iteration — lenient passthrough
  labels?: unknown[]; // 🔸 later iteration — lenient passthrough
};

// Wire shape observed on elis.develop.r8.lol (2026-07-23). Entries carry NO stable id —
// FE derives React keys from timestamp + index. Container is `content.changes`
// (draft-contract Q-E resolved: not `details`).
export type AuditLogEntry = {
  organizationId: number;
  timestamp: string; // ISO 8601
  username: string | null; // null on the wire even for user actions
  objectId: number; // = annotation id
  objectType: AuditLogObjectType;
  action: AuditLogAction;
  sourceType: AuditLogSourceType | null; // null = unattributed
  sourceDetails: AuditLogSourceDetails | null;
  // requestId groups the entries produced by one request (= one user action).
  content: { requestId?: string | null; changes: AuditLogChanges };
};

// --- Narrowing helpers (the catch-all arm is excluded via Extract) ----------------

type KnownAnnotationFieldChange<F extends string> = Extract<
  AnnotationFieldChange,
  { field: F }
>;

export const isAnnotationStatusChange = (
  change: AnnotationFieldChange
): change is KnownAnnotationFieldChange<'status'> => change.field === 'status';

export const isAnnotationQueueIdChange = (
  change: AnnotationFieldChange
): change is KnownAnnotationFieldChange<'queue_id'> =>
  change.field === 'queue_id';

export const isAnnotationAutomationBlockerChange = (
  change: AnnotationFieldChange
): change is KnownAnnotationFieldChange<'automation_blocker'> =>
  change.field === 'automation_blocker';
